// vg-binding: P7.D-70
//
// apps/vendor/src/features/print-queue/PrinterColumn.tsx
//
// Phase 7 Wave 14 Task 54 — Kanban printer column (drop target).

import { useTranslation } from 'react-i18next';

import { OrderCard } from './OrderCard.js';
import { STAGE_CONTAINER_CLASS } from '../../lib/ui/statusPill.js';
import type { PrintQueueAssignment, PrintQueueColumn } from './api.js';

interface PrinterColumnProps {
  column: PrintQueueColumn;
  onDropAssignment?: (printerId: string, assignmentId: string) => void;
  onOpenDetail?: (assignment: PrintQueueAssignment) => void;
}

export function PrinterColumn({
  column,
  onDropAssignment,
  onOpenDetail,
}: PrinterColumnProps): JSX.Element {
  const { t } = useTranslation('orders');
  const isUnassigned = column.printer_id === '__unassigned__';
  const isStage = column.printer_id.startsWith('__stage_');
  const isDropTarget = !isUnassigned && !isStage;
  const stageKey = isStage
    ? column.printer_id.replace(/^__stage_|__$/g, '')
    : null;

  // Visual taxonomy: dashed primary tint for Unassigned, solid neutral for
  // printer lanes, distinct stage tints (review→success→ship) for the
  // post-print continuum. Stage columns are NOT drop targets — status
  // changes flow via the in-card transition button. Stage tints come from
  // the shared STAGE_CONTAINER_CLASS map so dark mode renders correctly.
  const baseContainer = 'flex w-64 shrink-0 flex-col gap-2 rounded-xl border p-3';
  const headerClass = isUnassigned
    ? `${baseContainer} border-dashed border-pw-primary/40 bg-pw-primary/5`
    : stageKey === 'quality_check'
      ? `${baseContainer} ${STAGE_CONTAINER_CLASS.review}`
      : stageKey === 'ready_to_ship'
        ? `${baseContainer} ${STAGE_CONTAINER_CLASS.success}`
        : stageKey === 'shipped'
          ? `${baseContainer} ${STAGE_CONTAINER_CLASS.ship}`
          : `${baseContainer} border-pw-border bg-pw-bg`;

  const emptyHint = isUnassigned
    ? t('printQueue.board.emptyUnassigned')
    : isStage
      ? t('printQueue.board.emptyStage')
      : t('printQueue.board.emptyPrinter');

  const badgeLabel = isUnassigned
    ? t('printQueue.board.unassignedPending', { count: column.assignments.length })
    : isStage
      ? t('printQueue.board.stageCount', {
          count: column.assignments.length,
        })
      : t(`printQueue.printerType.${column.printer_type}`, {
          defaultValue: column.printer_type,
        });

  return (
    <div
      data-testid={`print-queue-printer-column-${column.printer_id}`}
      onDragOver={(e) => {
        if (isDropTarget) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!isDropTarget) return;
        e.preventDefault();
        const id = e.dataTransfer.getData('text/plain');
        if (id) onDropAssignment?.(column.printer_id, id);
      }}
      className={headerClass}
    >
      <header className="flex items-center justify-between pb-2">
        <h3 className="text-sm font-semibold text-pw-text">{column.name}</h3>
        <span className="rounded bg-pw-surface px-2 py-0.5 text-[10px] uppercase text-pw-text-muted">
          {badgeLabel}
        </span>
      </header>
      <div className="flex flex-col gap-2">
        {column.assignments.length === 0 ? (
          <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-xs text-pw-text-muted">
            {emptyHint}
          </p>
        ) : (
          column.assignments.map((a) => (
            <OrderCard
              key={a.assignment_id}
              assignment={a}
              {...(onOpenDetail ? { onOpenDetail } : {})}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default PrinterColumn;
